import * as yup from 'yup';

import { isEditorContentEmpty, type QuestionMetaDTO } from '@/src/lib';
import { exportDynamicToPlain } from '@/src/utils';

export const getChoiceSchemaDTE = (fieldName: string = 'Text') => {
    return yup.object().shape({
        id: yup.number().required(),
        text: yup
            .string()
            .test(
                'not-empty',
                `${fieldName} cannot be empty.`,
                (value) => !isEditorContentEmpty(value || '')
            )
            .required(`${fieldName} is required`),
        text_es: yup.string().when('$isSpanishAllowed', {
            is: true,
            then: (schema) =>
                schema
                    .test(
                        'not-empty',
                        `${fieldName} (Spanish) cannot be empty.`,
                        (value) => !isEditorContentEmpty(value || '')
                    )
                    .required(`${fieldName} (Spanish) is required`),
            otherwise: (schema) => schema.optional(),
        }),
    });
};

export const getChoiceSchemaDEE = (fieldName: string = 'Text') => {
    return yup.object().shape({
        id: yup.number().required(),
        text: yup
            .string()
            .required(`${fieldName} is required`)
            .max(2000, `${fieldName} maximum length exceeded`),
        text_es: yup.string().when('$isSpanishAllowed', {
            is: true,
            then: (schema) =>
                schema
                    .required(`${fieldName} (Spanish) is required`)
                    .max(2000, `${fieldName} (Spanish) maximum length exceeded`),
            otherwise: (schema) => schema.optional(),
        }),
    });
};

export const metaSchema: yup.ObjectSchema<QuestionMetaDTO> = yup.object({
    questionTypeMasterId: yup
        .number()
        .typeError('Question type is required')
        .moreThan(0)
        .required(),
    keypadSetting: yup.array().of(yup.string().required()).required(),
    gradeLevelMasterIds: yup
        .array()
        .of(
            yup
                .number()
                .typeError('Grade id must be a number')
                .moreThan(0, 'Grade id must be greater than 0')
                .required()
        )
        .min(1, 'At least one grade must be selected')
        .required(),
    standards: yup
        .array()
        .of(
            yup
                .number()
                .typeError('Standard id must be a number')
                .moreThan(0, 'Standard id must be greater than 0')
                .required()
        )
        .required(),
    difficultyLevelMasterId: yup
        .number()
        .typeError('Difficulty is required')
        .moreThan(0, 'Select a difficulty')
        .required(),
    primaryClaimMasterId: yup
        .number()
        .typeError('Primary Claim is required')
        .moreThan(0, 'Select a primary claim')
        .required(),
    primaryTargetMasterId: yup
        .number()
        .typeError('Primary Target is required')
        .moreThan(0, 'Select a primary target')
        .required(),
    depthOfKnowledgeMasterId: yup
        .number()
        .typeError('Depth of Knowledge is required')
        .moreThan(0, 'Select a depth of knowledge')
        .required(),
    isPracticeQuestion: yup.boolean().required(),
    isHandScored: yup.boolean().required(),
    shouldShuffleOptions: yup.boolean().required(),
    shouldShowKeypad: yup.boolean().required(),
    isSpanishAllowed: yup.boolean().required(),
    score: yup.number().min(0).required(),
});

export function areItemsUnique(choices: any[], key: string = ''): boolean {
    if (!Array.isArray(choices)) return false;

    const values = choices?.map((choice) => (key ? choice[key] : choice)) || [];
    const plainValues = values.map((choice) => exportDynamicToPlain(choice));
    return new Set(plainValues).size === plainValues.length;
}

export function areDynamicItemsUnique(items: string[]): boolean {
    if (!Array.isArray(items)) return false;

    const plainItems = items.map((item) => exportDynamicToPlain(item));
    return new Set(plainItems).size === plainItems.length;
}
